import { format, parseISO } from 'date-fns';
import { AgnosticAttribute, cartGetters, productGetters } from '~/composables/propeller/getters';
import { nl } from 'date-fns/locale';
import { productAttributes } from '~/constants/products.module';
import { ProductArg } from '~/types/propeller';

type CartArg = Parameters<typeof cartGetters.getItems>[0]

interface OrderableProduct {
  orderable?: string
  inventory?: { totalQuantity?: number }
}

interface BundleItem {
  product: ProductArg
}

interface SubjectLike {
  name: string
  levelName: string
}

export const wrapElements = function (elements: NodeListOf<Element> | Element[], cssClass: string): void {
  if (!elements.length) return;
  elements.forEach((el) => {
    const wrapper = document.createElement("div");
    wrapper.setAttribute("class", cssClass);
    el.parentNode?.insertBefore(wrapper, el);
    wrapper.appendChild(el);
  });
};

/**
 * Level sort order for sorting the subjects on the following pages
 * - Step 4 on the onboarding page for teacher role
 * - Teacher's dashboard
 */
export const levelSortOrder = ["vmbo-b", "vmbo-k", "vmbo-gl-tl", "havo", "vwo"];

export function getSubjectsAlphabetical (subjects: SubjectLike[]): SubjectLike[] {
  return [...subjects].sort(
    (a, b) =>
      a.name.toUpperCase().localeCompare(b.name.toUpperCase()) ||
      levelSortOrder.indexOf(a.levelName) - levelSortOrder.indexOf(b.levelName)
  );
}

/**
 * Capitalize string helper
 */
export function capitalize (value: unknown): string {
  if (!value) return "";
  const str = value.toString();
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Check if a product can be ordered
 */
export function isOrderable (product?: OrderableProduct): boolean {
  return product?.orderable === "Y"
}

/**
 * Check if a product can be delivered right away
 */
export function isAvailable (product?: OrderableProduct): boolean {
  return !!product?.inventory?.totalQuantity &&
  product.inventory.totalQuantity > 0 &&
  product.orderable === "Y"
}

function forAllProductItems (testFunction: (product?: OrderableProduct) => boolean) {
  return (items?: { product?: OrderableProduct }[]): boolean => {
    if (!items || items.length === 0) {
      return false;
    }
    return items.every(({ product }) => testFunction(product));
  }
}

/**
 * Check if all products of a bundle can be ordered
 */
export const allBundleItemsOrderable = forAllProductItems(isOrderable);

/**
 * Check if all products of a bundle can be delivered right away
 */
export const allBundleItemsAvailable = forAllProductItems(isAvailable);

/**
 * Round to 2 decimals at most
 */
export function roundToTwoDecimals (n?: number): number {
  return (!n ? 0 : Math.round(n * 100) / 100)
}

/**
 * Get a number from 0 up to and including max, random or from an index
 */
export function getRandomNumberWithMax (max = 0, index?: number): number {
  if (index !== undefined) {
    return index % (max + 1);
  }
  return Math.floor(Math.random() * max);
}

/**
 * Get a blurred dummy card image for a freemium user
 */
export function getDummyCardImage (index?: number): string {
  return require(`~/assets/img/cards/dummy${getRandomNumberWithMax(14, index)}.jpg`)
}

export function constructSubjectFilter (sections?: string[], topics?: string[], parentTopics?: string[], showCheckedCards?: boolean, difficulty?: (string | number)[], examTypes?: string[]): string {
  let filterQuery = ''
  if (sections || topics) {
    filterQuery = "?";
  }
  if (sections) {
    sections.forEach((section) => {
      filterQuery =
        filterQuery +
        (!filterQuery.endsWith("?") ? "&" : "") +
        "sections[]=" +
        section;
    });
  }
  if (topics && topics.length > 0 && parentTopics && parentTopics.length === 1) {
    topics.forEach((topic) => {
      filterQuery = filterQuery + (!filterQuery.endsWith('?') ? '&' : '') + 'topicIds[]=' + topic;
    });
  } else if (parentTopics && parentTopics.length > 0) {
    parentTopics.forEach((topic) => {
      filterQuery = filterQuery + (!filterQuery.endsWith('?') ? '&' : '') + 'topicIds[]=' + topic;
    });
  }
  if (difficulty) {
    difficulty.forEach((difficultyLevel) => {
      filterQuery =
        filterQuery +
        (!filterQuery.endsWith("?") ? "&" : "") +
        "difficulty[]=" +
        difficultyLevel;
    });
  }
  if (examTypes) {
    examTypes.forEach((examType) => {
      filterQuery =
        filterQuery +
        (!filterQuery.endsWith("?") ? "&" : "") +
        "examTypes[]=" +
        examType;
    });
  }
  if (!showCheckedCards) {
    filterQuery =
      filterQuery +
      (!filterQuery.endsWith("?") ? "&" : "") +
      "hideLearned=" +
      !showCheckedCards;
  }

  return filterQuery;
}

interface TestAttempt {
  results?: { grade?: number }
}

// helper func because nuxt has a bug where it doesn't allow optional chaining inside the render
// helper func because nuxt has a bug where it doesn't allow optional chaining inside the render
export function getLatestScore (latestAttempt?: TestAttempt): number | undefined {
  return latestAttempt?.results?.grade
}

export function getLocalizedDate (iso?: string): string {
  if (!iso) {
    return "";
  }
  return format(parseISO(iso), "EEEE d MMMM", { locale: nl });
}

/**
 * Return a copy of an object, in which all string values have been trimmed (shallow)
 */
export function trimAllStringValues<T extends Record<string, unknown>> (object: T = {} as T): T {
  const result = { ...object };
  Object.keys(object).forEach((key) => {
    if (typeof object[key] === "string") {
      (result as Record<string, unknown>)[key] = (object[key] as string).trim();
    }
  });
  return result;
}

/**
 * Create slug from product name, replacing all spaces and slashes with a hyphen and transforming to all lowercase
 */
export function constructSlug (s?: string): string {
  return s?.split(/\s|\//).join("-").toLowerCase() ?? ""
}

/**
 * Joins product names of product items in bundle together to show product type
 */
export function getBundleProductType (bundleItems?: BundleItem[]): string | undefined {
  return bundleItems
    ?.map(
      (bundleItem) =>
        (productGetters.getAttributes(bundleItem.product, [
          productAttributes.brand,
        ])[0] as AgnosticAttribute | undefined)?.value
    )
    .join(" + ")
}
/**
 * Get a number with a min and/or max value
 */
export function getNumberInBounds (n: number, min?: number, max?: number): number {
  if (min !== undefined && n < min) {
    return min;
  }
  if (max !== undefined && n > max) {
    return max;
  }
  return n;
}

const BTW_RATE = 9;

/**
 * formats the total price incl btw from cartGetters.getTotals to an object containing the price excl btw & the total btw amount
 *
 * Rounds to cents here instead of leaving it to `formatPrice`: an exact split of
 * 58.03596 is 53.24400 + 4.79196, which renders as "53,24" + "4,79" — one cent
 * short of the "58,04" printed right underneath it (EXAMS-138 QA). Deriving the
 * btw from the two already-rounded figures keeps the rows adding up to the
 * total. Display only; the amount charged is untouched.
 */
export function formatTotals (totalPrice: number) {
  const inclBtw = roundToTwoDecimals(totalPrice);
  const exBtw = roundToTwoDecimals((totalPrice * 100) / (100 + BTW_RATE));
  const btwAmount = roundToTwoDecimals(inclBtw - exBtw);
  return {
    btwAmount,
    exBtw,
    inclBtw,
  };
}

/**
 * Formats examtype name (either 'oriëntatietoets' or 'examen') in the correct grammatical format based on input
 */
export function formatExamTypeText (examType: string, capitalized = false, withArticle = false, withDemonstrativePronoun = false): string {
  const isSelfTest = examType === "oriëntatietoets";
  let preposition = "";

  if (withArticle) {
    preposition = isSelfTest ? "de " : "het ";
  }

  if (withDemonstrativePronoun) {
    preposition = isSelfTest ? "deze " : "dit ";
  }

  return capitalized
    ? capitalize(preposition + examType)
    : preposition + examType;
}

const orderedLevelsList = [
  "vmbo",
  "vmbo-b",
  "vmbo-gl/tl",
  "vmbo-k",
  "vmbo-t",
  "havo",
  "vwo",
  "mbo",
  "hbo",
  "wo",
];

/**
 * Sort list of education levels (vmbo etc.) in a somewhat arbitrary sensible way
 */
// Values the backend wrongly exposes in the onderwijstype facet but which are
// NOT school levels (they belong under producttype). Excluded from the
// schoolniveau filter so e.g. "Voordeelpakket" can't be picked as a level.
const nonLevelValues = ["voordeelpakket"];

export function sortLevelsList (levelsList: string[]): string[] {
  const sorted: string[] = [];
  const rest: string[] = [];

  levelsList
    .filter((level) => !nonLevelValues.includes(level.toLowerCase()))
    .forEach((level) => {
    if (!orderedLevelsList.includes(level.toLowerCase())) {
      rest.push(level);
      return;
    }
    sorted.push(level);
  });

  return [
    ...sorted.sort((a, b) => {
      const aIndex = orderedLevelsList.indexOf(a.toLowerCase());
      const bIndex = orderedLevelsList.indexOf(b.toLowerCase());
      if (aIndex > bIndex) return 1;
      if (aIndex < bIndex) return -1;
      return 0;
    }),
    ...rest.sort(),
  ];
}

interface TrackingProduct {
  bundels?: unknown
  name: string
  eanCode?: string
  id: string | number
  price: { net: number; discount?: number }
  quantity?: number
  productType?: { value?: string }
  level?: string
}

/**
 * constructs product list to send as tracking data to Google Analytics
 */
export function setProductsListTracking (products: TrackingProduct[]) {
  return products.map(setProductTracking)
}

/**
 * constructs a single product to send as tracking data to Google Analytics
 */
export function setProductTracking (product: TrackingProduct, index?: number) {
  if (product.bundels) {
    return;
  }

  return {
    item_name: product.name,
    item_id: product.eanCode ?? product.id,
    price: Number(product.price?.net.toFixed(2)) || 0.0,
    item_brand: "ThiemeMeulenhoff",
    quantity: product.quantity || null,
    item_category: product.productType?.value || "Examenbundel",
    item_category2: product.level,
    discount: product.price?.discount
      ? Number(product.price.discount.toFixed(2))
      : 0.0,
    currency: "EUR",
    index,
  };
}

export function formatPrice (price: number, showDecimals?: boolean): string {
  const amount = Intl.NumberFormat("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
  return showDecimals ? amount : amount.replace(",00", ",-");
}

/**
 * Total discount on the cart, incl. VAT — the amount shown on the "Korting" row.
 *
 * Takes the cart itself, NOT the Vue ref. It used to accept the ref and read
 * `cart.total.discountNet` off it, which is always `undefined` on a ref, so an
 * order-level discount was silently dropped.
 *
 * Per line the discount is the difference between the gross list total and what
 * Propeller actually charges. The previous version multiplied `priceNet` (the
 * ALREADY discounted unit price) by the discount percentage, which understates
 * the discount — on a live acceptance cart it produced 1.62 where 1.70 is right,
 * and the resulting "was" price no longer added up to the total.
 */
export function calculateDiscountFromCart (cart?: CartArg | null): number {
  return (cart?.total?.discountNet ?? 0) + cartGetters.getItems(cart).reduce((acc, item) => {
    // getItems yields `[{}]` for an empty cart (quirk kept from v1).
    if (!item?.quantity) { return acc; }
    return acc + (cartGetters.getItemGrossListTotal(item) - item.totalPriceNet);
  }, 0);
}
